import { createTheme } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

import {
  listItemAvatarTheme,
  listItemIconTheme,
  listItemTextTheme
} from '@/design-system/list/listTheme';
import { linearProgressTheme } from '@/design-system/progress/progressTheme';
import { primitive } from '@/design-system/tokens/primitive';
import { semantic } from '@/design-system/tokens/semantic';
import { tooltipTheme } from '@/design-system/tooltip/tooltipTheme';
import { typographyTheme } from '@/design-system/typography/typographyTheme';
import { typography } from '@/design-system/typography/typographyVariants';

interface NeutralPalette {
  caption: string;
  border: string;
  hover: string;
  disabledBackground: string;
  disabledText: string;
  white: string;
  shadow: string;
}

declare module '@mui/material/styles' {
  interface PaletteColor {
    surface: string;
  }

  interface SimplePaletteColorOptions {
    surface?: string;
  }

  interface Palette {
    neutral: NeutralPalette;
    primitive: typeof primitive;
    semantic: typeof semantic;
  }

  interface PaletteOptions {
    neutral?: Partial<NeutralPalette>;
    primitive?: typeof primitive;
    semantic?: typeof semantic;
  }
}

let theme = createTheme();
theme = createTheme({
  cssVariables: { cssVarPrefix: '' },
  palette: {
    primitive,
    semantic,
    primary: {
      main: primitive.blue[700],
      dark: primitive.blue[900],
      light: primitive.blue[50],
      contrastText: primitive.gray[0]
    },
    secondary: {
      main: primitive.gray[900],
      light: primitive.blue[700]
    },
    text: {
      primary: primitive.gray[900],
      secondary: primitive.gray[700]
    },
    success: {
      main: primitive.green[700],
      contrastText: primitive.gray[0],
      dark: primitive.green[700],
      light: '#67CC71',
      surface: primitive.green[100]
    },
    info: {
      main: primitive.blue[200],
      light: primitive.blue[100],
      dark: 'rgba(0, 0, 0, 0.08)'
    },
    error: {
      main: primitive.red[700],
      dark: primitive.red[900],
      contrastText: primitive.gray[0],
      surface: primitive.red[100]
    },
    warning: {
      main: primitive.orange[700],
      contrastText: primitive.gray[0]
    },
    neutral: {
      caption: primitive.gray[500],
      border: semantic.border.secondary,
      hover: primitive.blue[300],
      disabledBackground: primitive.gray[100],
      disabledText: primitive.gray[300],
      white: primitive.gray[0],
      shadow: primitive.gray[500]
    }
  },
  typography,
  components: {
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: semantic.text.disabled,
          '& .MuiTypography-root': {
            fontSize: '16px'
          }
        }
      }
    },
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        slotProps: {
          heading: {
            component: 'div'
          }
        }
      }
    },
    MuiAccordionSummary: {
      defaultProps: {
        // MUI v6 renders the summary root as a native <button> (v5 used a
        // <div role="button">). Several summaries host interactive buttons
        // (e.g. facesheet print/edit), which produces invalid nested-button
        // HTML. Restore the v5 element to keep the markup valid.
        slotProps: {
          root: {
            component: 'div'
          }
        }
      },
      styleOverrides: {
        root: {
          fontFamily: 'Roboto'
        },
        content: {
          fontSize: '16px'
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          background: primitive.gray[300]
        }
      }
    },
    MuiCard: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundColor: 'inherit'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '& .MuiChip-label': {
            paddingLeft: '10px',
            paddingRight: '10px',
            fontSize: '13px'
          },
          '&.MuiChip-colorError, &.MuiChip-colorSuccess': {
            color: semantic.text.inverse
          }
        },
        sizeSmall: {
          '& .MuiChip-label': {
            paddingLeft: '8px',
            paddingRight: '8px'
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        size: 'large',
        disableElevation: true
      },
      styleOverrides: {
        sizeSmall: {
          height: 30,
          padding: '4px 10px',
          lineHeight: 2.75
        },
        sizeMedium: {
          height: 36,
          padding: '6px 16px'
        },
        sizeLarge: {
          height: 42,
          padding: '8px 22px'
        },
        root: {
          minWidth: 'max-content',
          letterSpacing: 'unset',
          textTransform: 'none',
          borderRadius: '100px'
        },
        // TODO: Normalize button theme pseudoclasses after chatting with design
        outlinedPrimary: {
          color: semantic.text.accent,
          backgroundColor: 'unset',
          borderColor: semantic.border.primary,
          '&:hover': {
            color: semantic.text.accent,
            backgroundColor: 'none',
            borderColor: semantic.border.primary
          }
        },
        outlinedSecondary: {
          color: semantic.text.secondary,
          borderColor: primitive.gray[700],
          background: 'none',
          '&:hover': {
            color: semantic.text.secondary,
            borderColor: primitive.gray[700],
            background: 'none'
          }
        },
        outlinedInfo: {
          color: semantic.text.caption,
          borderColor: semantic.border.secondary,
          background: semantic.background.base,
          '&:hover': {
            color: semantic.text.caption,
            borderColor: semantic.border.secondary,
            background: semantic.background['base-hover']
          }
        },
        containedPrimary: {
          color: semantic.text.inverse,
          background: semantic.background.primary,
          '&:hover': {
            color: semantic.text.inverse,
            background: semantic.background.primary
          }
        },
        containedSecondary: {
          color: semantic.text.secondary,
          backgroundColor: semantic.background.secondary,
          '&:hover': {
            color: semantic.text.secondary,
            backgroundColor: semantic.background['secondary-hover']
          }
        },
        textPrimary: {
          width: 'max-content',
          background: 'unset',
          color: semantic.text.accent
        },
        textSecondary: {
          background: 'none'
        }
      }
    },
    MuiMobileDatePicker: {
      defaultProps: {
        slotProps: {
          dialog: {
            sx: {
              '& .MuiPaper-root': {
                width: '100dvw',
                height: '100dvh',
                maxHeight: 'unset',
                borderRadius: 0,
                margin: 0
              }
            }
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: semantic.divider.primary,
          '&::before, &::after': {
            borderTopColor: semantic.divider.primary
          }
        }
      }
    },
    MuiDrawer: {
      defaultProps: {
        anchor: 'right'
      },
      styleOverrides: {
        paper: {
          width: 560,
          overflowX: 'hidden',
          overflowY: 'auto',
          [theme.breakpoints.down('sm')]: {
            width: '100vw',
            height: '100dvh'
          }
        }
      }
    },
    MuiRadio: {
      defaultProps: {
        size: 'small'
      }
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          height: 'max-content',
          background: semantic.background.secondary,
          borderRadius: '360px'
        }
      }
    },
    MuiToggleButton: {
      defaultProps: {
        size: 'large'
      },
      styleOverrides: {
        sizeLarge: {
          height: 42,
          fontSize: '15px',
          padding: '0 22px'
        },
        sizeMedium: {
          height: 36,
          fontSize: '14px',
          padding: '0 16px'
        },
        root: {
          width: 'max-content',
          transition: 'background 200ms ease-in-out',
          fontWeight: 500,
          background: semantic.background.secondary,
          borderRadius: '360px',
          textTransform: 'none',
          border: 'none',
          letterSpacing: 'unset',
          color: semantic.text.secondary,
          '&:hover': {
            background: semantic.background.secondary
          },
          '&.Mui-selected': {
            background: semantic.background['secondary-hover'],
            color: semantic.text.primary,
            '&:hover': {
              background: semantic.background['secondary-hover']
            }
          },
          '&.Mui-disabled': {
            border: 'none'
          }
        }
      }
    },
    MuiTooltip: tooltipTheme,
    MuiFormControl: {
      styleOverrides: {
        root: {
          '&.radio-group,&.checkbox-group': {
            '& .MuiFormLabel-root': {
              color: semantic.text.primary,
              fontSize: '16px',
              fontWeight: 500,
              '&.Mui-error': {
                color: primitive.red[700]
              }
            }
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '14px',
          color: semantic.text.primary
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: theme.palette.error.main
        }
      }
    },
    MuiDataGrid: {
      defaultProps: {
        disableVirtualization: true,
        hideFooterSelectedRowCount: true,
        pagination: true,
        disableRowSelectionOnClick: true,
        disableColumnMenu: true,
        hideFooter: false,
        getRowHeight: () => 'auto',
        getDetailPanelHeight: () => 'auto',
        slotProps: {
          panel: {
            placement: 'bottom-end'
          }
        }
      },
      styleOverrides: {
        root: {
          border: 'none',
          background: semantic.background.base,
          borderRadius: '16px',
          [theme.breakpoints.down('sm')]: {
            borderRadius: 0
          }
        },
        cell: {
          borderColor: semantic.divider.primary,
          paddingTop: '15px',
          paddingBottom: '15px',
          [theme.breakpoints.up('lg')]: {
            ':not(last-of-type)': {
              paddingLeft: '24px'
            },
            ':last-of-type': {
              paddingRight: '24px'
            }
          },
          [theme.breakpoints.down('lg')]: {
            fontSize: '0.875rem'
          },
          [theme.breakpoints.down('sm')]: {
            borderBottom: 'none',
            ':not(.ai-analysis-cell)': {
              minWidth: 'unset !important',
              maxWidth: 'unset !important',
              width: '100vw'
            },
            minHeight: 'unset !important',
            maxHeight: 'unset !important',
            padding: 0
          }
        },
        columnHeaders: {
          background: `linear-gradient(0deg, rgba(0, 102, 138, 0.05), rgba(0, 102, 138, 0.05)), ${semantic.background.base}`,
          borderRadius: 0,
          borderBottom: 'unset',
          [theme.breakpoints.up('lg')]: {
            ':not(last-of-type)': {
              paddingLeft: '14px'
            },
            ':last-of-type': {
              paddingRight: '14px'
            }
          },
          [theme.breakpoints.down('lg')]: {
            fontSize: '0.875rem'
          },
          [theme.breakpoints.down('sm')]: {
            display: 'none'
          }
        },
        columnHeaderTitle: {
          textOverflow: 'clip',
          whiteSpace: 'break-spaces',
          lineHeight: 1.1
        },
        columnHeaderTitleContainer: {
          textOverflow: 'clip',
          whiteSpace: 'break-spaces',
          lineHeight: 1.1
        },
        detailPanelToggleCell: {
          '&.Mui-disabled': {
            display: 'none'
          }
        },
        menu: {
          '& .MuiPaper-root': {
            background: semantic.background.base,
            boxShadow: '0px 5px 20px 2px rgba(44, 58, 66, 0.15)',
            borderRadius: '8px'
          },
          '& .MuiMenuItem-root': {
            height: 48,
            '&:hover': {
              background: semantic.background.secondary
            }
          }
        },
        paper: {
          minWidth: 'unset',
          overflow: 'hidden',
          boxShadow: '0px 5px 20px 2px rgba(44, 58, 66, 0.15)'
        },
        row: {
          '&:hover': {
            background: `linear-gradient(0deg, rgba(0, 102, 138, 0.05), rgba(0, 102, 138, 0.05)), ${semantic.background.base}`
          }
        }
      }
    },
    MuiListItemText: listItemTextTheme,
    MuiListItemAvatar: listItemAvatarTheme,
    MuiListItemIcon: listItemIconTheme,
    MuiMenu: {
      styleOverrides: {
        root: {
          background: 'unset'
        },
        list: {
          boxShadow: '0px 5px 20px 2px rgba(44, 58, 66, 0.15)',
          borderRadius: '8px'
        }
      }
    },
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable',
        scrollButtons: 'auto'
      },
      styleOverrides: {
        root: {
          width: '100%',
          zIndex: '2 !important',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          minHeight: 57, // The height must be at least 1px large than the height
          // of .MuiButtonBase-root in order to not show the scroll arrows
          // by default
          backgroundColor: semantic.background.base,
          borderBottom: `1px ${semantic.border.tertiary} solid`,
          padding: '0 24px',
          '& .MuiTab-root': {
            minHeight: 56 // See above comment
          },
          [theme.breakpoints.down('lg')]: {
            padding: 0
          },
          [theme.breakpoints.down('sm')]: {
            borderRadius: 0,
            minHeight: 49,
            '& .MuiTab-root': {
              minHeight: 48
            }
          }
        },
        indicator: {
          backgroundColor: semantic.background.primary
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          letterSpacing: '0.1px',
          '&.Mui-selected': {
            color: semantic.text.accent
          }
        },
        labelIcon: {
          color: semantic.text.secondary,
          marginRight: 8
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        sizeSmall: {
          height: 24,
          borderRadius: 8,
          fontSize: 13,
          '& .MuiSelect-select': {
            paddingLeft: 9,
            paddingTop: 5,
            paddingRight: '24px !important'
          },
          '& .MuiSvgIcon-root': {
            right: 1
          }
        },
        root: {
          height: 42,
          backgroundColor: semantic.background.secondary,
          borderRadius: 360,
          color: semantic.text.secondary,
          '&::before, &::after': {
            display: 'none'
          },
          '&:hover': {
            backgroundColor: semantic.background['secondary-hover']
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: semantic.text.caption
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '& tbody > tr:last-of-type > td': {
            borderBottom: 'none'
          }
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {
          border: `1px solid ${semantic.divider.primary}`,
          borderRadius: '8px'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background: `linear-gradient(0deg, rgba(0, 102, 138, 0.05), rgba(0, 102, 138, 0.05)), ${semantic.background.base}`,
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${semantic.border.tertiary}`
          }
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          color: semantic.text.primary,
          padding: '24px !important',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          overflowX: 'hidden',
          [theme.breakpoints.down('sm')]: {
            padding: '16px !important'
          }
        }
      }
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          padding: 'unset',
          fontSize: '16px',
          color: semantic.text.primary
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '18px',
          fontWeight: 500,
          color: semantic.text.primary,
          display: 'flex',
          padding: '24px 24px 0 24px',
          justifyContent: 'space-between',
          alignItems: 'center',
          [theme.breakpoints.down('sm')]: {
            padding: '16px 16px 0 16px'
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 5px 10px 0px rgba(117, 136, 153, 0.10)',
          borderRadius: '16px',
          background: semantic.background.base,
          minWidth: 480,
          width: 'max-content',
          display: 'flex',
          [theme.breakpoints.down('sm')]: {
            minWidth: 'unset',
            width: '100%'
          }
        },
        paperFullScreen: {
          borderRadius: 0
        },
        paperWidthXs: {
          maxWidth: '480px'
        },
        paperWidthSm: {
          maxWidth: '480px'
        },
        paperWidthMd: {
          maxWidth: '620px'
        },
        paperWidthLg: {
          maxWidth: '820px'
        },
        paperWidthXl: {
          maxWidth: '820px'
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0 24px 24px 24px',
          gap: '8px',
          '& > :not(:first-of-type)': {
            marginLeft: 0
          },
          [theme.breakpoints.down('sm')]: {
            padding: '0 16px 16px 16px'
          }
        }
      }
    },
    MuiTypography: typographyTheme,
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          color: 'inherit',
          fontSize: '1rem'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px'
        },
        outlinedInfo: {
          color: `${semantic.text.primary} !important`,
          border: `1px solid ${semantic.border.primary} !important`,
          '& .MuiAlert-icon': {
            color: `${semantic.text.accent} !important`
          }
        },
        standardInfo: {
          color: 'rgba(0, 0, 0, 0.87)',
          background: `linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), ${primitive.blue[900]}`,
          '& .MuiAlert-icon': {
            color: `${semantic.text['accent-strong']} !important`
          }
        },
        standardError: {
          '& .MuiAlert-icon': {
            color: `${primitive.red[700]} !important`
          }
        },
        standardSuccess: {
          '& .MuiAlert-icon': {
            color: `${semantic.text.success} !important`
          }
        },
        standardWarning: {
          '& .MuiAlert-icon': {
            color: `${primitive.orange[700]} !important`
          }
        }
      }
    },
    MuiLinearProgress: linearProgressTheme
  }
});

export default theme;

import { VictoryTheme } from 'victory';

const chart = { ...VictoryTheme.material } as typeof VictoryTheme.material;
if (chart.group?.colorScale && Array.isArray(chart.group.colorScale)) {
  chart.group.colorScale = [];
}

const themes = { ...VictoryTheme, exacare: chart };

export { themes as VictoryTheme };
